import { ImgWithFallback } from "@/components/img-with-fallback";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  AD_ANIMATOR_DURATIONS,
  AD_ANIMATOR_ASPECT_RATIOS,
  AD_ANIMATOR_INTENSITIES,
  AD_ANIMATOR_MUSIC_STYLES,
  AD_ANIMATOR_VARIATION_OPTIONS,
  buildAdAnimatorPrompt,
  type AdAnimatorDuration,
  type AdAnimatorAspectRatio,
  type AdAnimatorIntensity,
  type AdAnimatorMusicStyle,
} from "@shared/ad-animator";
import type {
  GeneratedAd,
  AdAnimationJob,
  AdAnimationOutput,
  Brand,
  Persona,
  Product,
  NarrativeAngle,
  AdUnitType,
} from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/lib/picker-search";
import {
  Clapperboard,
  Loader2,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Check,
  X,
  ArrowRight,
  ArrowLeft,
  Search,
  Bookmark,
  Trash2,
  Pencil,
  MoreHorizontal,
  ThumbsUp,
  MessageCircle,
  Share2,
  Image as ImageIcon,
  Volume2,
  Wand2,
} from "lucide-react";

type LibraryAd = GeneratedAd & { durableUrl: string | null };
type AnimationJob = AdAnimationJob & {
  videos: AdAnimationOutput[];
  personaName: string | null;
  productName: string | null;
  angleName: string | null;
  adUnitName: string | null;
};

const TERMINAL_STATUSES = ["completed", "partial", "failed", "cancelled"];

export default function AdAnimatorPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();

  // Brand info powers the Facebook-post chrome in the Step 2 phone mockup.
  const { data: brand } = useQuery<Brand>({
    queryKey: [`/api/brands/${brandId}`],
    enabled: !!brandId,
  });

  // Two-step flow: 1 = pick the saved ads, 2 = configure + animate.
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedAdIds, setSelectedAdIds] = useState<string[]>([]);
  const [duration, setDuration] = useState<AdAnimatorDuration>(10);
  const [aspectRatio, setAspectRatio] = useState<AdAnimatorAspectRatio>("Original");
  const [intensity, setIntensity] = useState<AdAnimatorIntensity>("Standard");
  const [musicStyle, setMusicStyle] = useState<AdAnimatorMusicStyle>("No Music");
  const [customMusicStyle, setCustomMusicStyle] = useState("");
  const [variations, setVariations] = useState<number>(1);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  // Prompt editing (Step 2). Two layered modes: a short "Add instructions"
  // note appended to the proven base prompt (default), or a full freeform
  // "Edit full prompt" override used verbatim.
  const [customInstructions, setCustomInstructions] = useState("");
  const [fullPromptMode, setFullPromptMode] = useState(false);
  const [promptOverride, setPromptOverride] = useState("");

  // The live base prompt for the current settings (no override applied). Shown
  // as a preview and used to seed the full-prompt textarea when toggled on.
  const basePrompt = useMemo(
    () =>
      buildAdAnimatorPrompt({
        musicStyle,
        customMusicStyle: musicStyle === "Custom" ? customMusicStyle : null,
        intensity,
        customInstructions,
      }),
    [musicStyle, customMusicStyle, intensity, customInstructions],
  );

  // The prompt actually sent: the override verbatim when in full-prompt mode
  // with content, otherwise the base + instructions.
  const effectivePrompt =
    fullPromptMode && promptOverride.trim() ? promptOverride : basePrompt;

  // Regenerate dialog state — seeded from an existing job to tweak + re-run.
  const [regenJob, setRegenJob] = useState<AnimationJob | null>(null);

  // History / Saved view. Freshly generated animations are reviewed up top in
  // the phone beside Settings; the list below is just the full History and the
  // Saved set.
  const [view, setView] = useState<"history" | "saved">("history");
  // Job ids generated in this browser session. These (plus anything still
  // running) populate the Results tab so users see what they just made without
  // it being mixed into the full History list.
  const [sessionJobIds, setSessionJobIds] = useState<string[]>([]);
  // Session jobs the user has already swiped on (saved or discarded) in the
  // phone review. They leave the review queue and drop into the grid below.
  const [reviewedJobIds, setReviewedJobIds] = useState<string[]>([]);
  const [historySearch, setHistorySearch] = useState("");
  const [historySort, setHistorySort] = useState<"newest" | "oldest">("newest");

  // Step 1 search / sort / filter — same controls as the My Ads library so
  // browsing a large saved-ad set feels familiar.
  const [search, setSearch] = useState("");
  const [personaId, setPersonaId] = useState("all");
  const [productId, setProductId] = useState("all");
  const [angleId, setAngleId] = useState("all");
  const [adUnitId, setAdUnitId] = useState("all");
  const [sort, setSort] = useState<"newest" | "oldest">("newest");

  // Reference data for the filter dropdowns.
  const { data: personas = [] } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  const { data: products = [] } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: angles = [] } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
  });
  const { data: adUnits = [] } = useQuery<AdUnitType[]>({
    queryKey: [`/api/brands/${brandId}/ad-units`],
    enabled: !!brandId,
  });

  // Build the listing URL. useMemo keeps the React Query key stable so we
  // only refetch when a filter actually changes.
  const listUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("status", "saved");
    params.set("scope", "brand");
    // The list defaults to 200 rows; raise to the server max (500) so the
    // full saved-ad set is selectable. Filters/search narrow it further.
    params.set("limit", "500");
    if (personaId !== "all") params.set("personaId", personaId);
    if (productId !== "all") params.set("productId", productId);
    if (angleId !== "all") params.set("narrativeAngleId", angleId);
    if (adUnitId !== "all") params.set("adUnitTypeId", adUnitId);
    if (search.trim()) params.set("q", search.trim());
    return `/api/brands/${brandId}/generated-ads?${params.toString()}`;
  }, [brandId, personaId, productId, angleId, adUnitId, search]);

  // Saved ads to choose from. Brand-wide (scope=brand) so collaborators can
  // animate any saved ad in the brand, matching the brand-wide history.
  //
  // Resilience: a single transient 401 (e.g. a request that races page load
  // before the session is attached) must NOT wedge this query forever. The
  // global defaults are retry:false + staleTime:Infinity, which would cache
  // that one failure permanently and leave the grid stuck. We override both
  // here so a brief blip self-heals and a manual Retry always works.
  const {
    data: rawAds = [],
    isLoading: adsLoading,
    isError: adsError,
    refetch: refetchAds,
  } = useQuery<LibraryAd[]>({
    queryKey: [listUrl],
    enabled: !!brandId,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    staleTime: 30_000,
  });

  // The Ad Animator only animates still images; video library entries (e.g.
  // Video Express Max final cuts saved into My Ads) can't be re-animated, so
  // exclude them from the picker.
  const ads = useMemo(() => rawAds.filter((a) => a.mediaType !== "video"), [rawAds]);

  // Client-side sort of the fetched page (the API has no sort param).
  const displayedAds = useMemo(() => {
    const arr = [...ads];
    arr.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sort === "oldest" ? ta - tb : tb - ta;
    });
    return arr;
  }, [ads, sort]);

  // Accumulate every ad we've ever fetched (across filter changes) so a
  // selected ad's thumbnail still renders in the Step 2 summary even after
  // the active filter excludes it from the current grid.
  const [knownAds, setKnownAds] = useState<Record<string, LibraryAd>>({});
  useEffect(() => {
    if (ads.length === 0) return;
    setKnownAds((prev) => {
      const next = { ...prev };
      for (const a of ads) next[a.id] = a;
      return next;
    });
  }, [ads]);

  const hasActiveFilters =
    search.trim() !== "" ||
    personaId !== "all" ||
    productId !== "all" ||
    angleId !== "all" ||
    adUnitId !== "all";

  const selectedAds = useMemo(
    () => selectedAdIds.map((id) => knownAds[id]).filter(Boolean) as LibraryAd[],
    [knownAds, selectedAdIds],
  );

  // Brand chrome for the phone mockup's "Sponsored" Facebook post.
  const brandName = brand?.name || "Your Brand";
  const brandLogo = brand?.logoUrl || brand?.adgenLogoUrl || null;
  const siteHost = useMemo(() => {
    try {
      return brand?.url
        ? new URL(brand.url).host.replace(/^www\./, "")
        : "yourstore.com";
    } catch {
      return "yourstore.com";
    }
  }, [brand?.url]);

  // History of past jobs. Keep polling whenever any job is still running so a
  // job resumed on server restart (or started in another tab) keeps updating
  // here even after a refresh clears local activeJobId state.
  const { data: history = [] } = useQuery<AnimationJob[]>({
    queryKey: [`/api/brands/${brandId}/ad-animator/history`],
    enabled: !!brandId,
    refetchInterval: (query) => {
      const jobs = (query.state.data as AnimationJob[] | undefined) || [];
      const hasRunning = jobs.some((j) => !TERMINAL_STATUSES.includes(j.status));
      return hasRunning || activeJobId ? 3000 : false;
    },
  });

  // Jobs still being generated show in the top "In progress" section; only
  // once a job reaches a terminal state does it move down into History. This
  // keeps freshly-started animations out of History (they were never "made").
  const runningJobs = useMemo(
    () => history.filter((j) => !TERMINAL_STATUSES.includes(j.status)),
    [history],
  );
  const finishedJobs = useMemo(
    () => history.filter((j) => TERMINAL_STATUSES.includes(j.status)),
    [history],
  );

  // Results tab: everything still running (so a resumed job is never hidden)
  // plus only the finished jobs generated in this session. Newest first.
  const resultsFinished = useMemo(() => {
    const ids = new Set(sessionJobIds);
    return finishedJobs
      .filter((j) => ids.has(j.id))
      .sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
  }, [finishedJobs, sessionJobIds]);

  // Phone review queue: finished session jobs that have a playable video and
  // haven't been swiped yet. The user plays each and keeps (save) or trashes it.
  const pendingReview = useMemo(
    () =>
      resultsFinished.filter(
        (j) =>
          (j.videos || []).some((v) => v.status === "ready" && v.videoUrl) &&
          !reviewedJobIds.includes(j.id),
      ),
    [resultsFinished, reviewedJobIds],
  );
  // The History/Saved tab list: finished jobs (or just the saved ones),
  // filtered by the search box and ordered by the sort toggle.
  const displayedJobs = useMemo(() => {
    let arr = finishedJobs;
    if (view === "saved") arr = arr.filter((j) => j.saved);
    const q = historySearch.trim().toLowerCase();
    if (q) {
      arr = arr.filter((j) =>
        [
          j.personaName,
          j.productName,
          j.angleName,
          j.adUnitName,
          j.status,
          j.musicStyle,
          j.aspectRatio,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
    }
    return [...arr].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return historySort === "oldest" ? ta - tb : tb - ta;
    });
  }, [finishedJobs, view, historySearch, historySort]);

  const savedCount = useMemo(
    () => finishedJobs.filter((j) => j.saved).length,
    [finishedJobs],
  );

  // Once the job we just started reaches a terminal state, drop the local
  // pointer so history polling can stop (the finished job now lives in
  // History; any still-running job keeps polling alive on its own).
  useEffect(() => {
    if (!activeJobId) return;
    const job = history.find((j) => j.id === activeJobId);
    if (job && TERMINAL_STATUSES.includes(job.status)) {
      setActiveJobId(null);
    }
  }, [history, activeJobId]);

  const generateMutation = useMutation({
    mutationFn: async () => {
      // One job per selected ad. Each POST is isolated in try/catch so a
      // single failure never aborts the rest — we report partial success.
      const jobIds: string[] = [];
      let failed = 0;
      let lastError = "";
      for (const adId of selectedAdIds) {
        try {
          const res = await apiRequest("POST", `/api/brands/${brandId}/ad-animator/generate`, {
            adId,
            duration,
            aspectRatio,
            animationIntensity: intensity,
            musicStyle,
            customMusicStyle: musicStyle === "Custom" ? customMusicStyle : undefined,
            variations,
            customInstructions: customInstructions.trim() || undefined,
            promptOverride:
              fullPromptMode && promptOverride.trim() ? promptOverride : undefined,
          });
          const data = await res.json();
          if (data?.jobId) jobIds.push(data.jobId);
        } catch (e: any) {
          failed += 1;
          lastError = e?.message || "Could not start an animation.";
        }
      }
      return { jobIds, failed, lastError };
    },
    onSuccess: ({ jobIds, failed, lastError }: { jobIds: string[]; failed: number; lastError: string }) => {
      if (jobIds.length > 0) {
        setActiveJobId(jobIds[0]);
        // Track these as this session's jobs so the phone review queue and the
        // History list pick them up.
        setSessionJobIds((prev) => [...prev, ...jobIds]);
      }
      // Always refresh history — some jobs may have started even if others failed.
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/ad-animator/history`],
      });
      if (jobIds.length === 0) {
        toast({
          title: "Could not start",
          description: lastError || "None of the animations could be started.",
          variant: "destructive",
        });
        return;
      }
      if (failed > 0) {
        toast({
          title: `${jobIds.length} started, ${failed} failed`,
          description: lastError || "Some animations could not be started.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: jobIds.length > 1 ? `${jobIds.length} animations started` : "Animation started",
        description: "Your video is being generated.",
      });
    },
    onError: (e: any) => {
      toast({ title: "Could not start", description: e.message, variant: "destructive" });
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/ad-animator/jobs/${jobId}/retry`, {});
      return res.json();
    },
    onSuccess: (data: { jobId: string }) => {
      setActiveJobId(data.jobId);
      if (data.jobId) {
        setSessionJobIds((prev) =>
          prev.includes(data.jobId) ? prev : [...prev, data.jobId],
        );
      }
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/ad-animator/history`],
      });
      toast({ title: "Retrying", description: "Re-running the animation." });
    },
    onError: (e: any) => {
      toast({ title: "Retry failed", description: e.message, variant: "destructive" });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ jobId, saved }: { jobId: string; saved: boolean }) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/ad-animator/jobs/${jobId}/save`,
        { saved },
      );
      return res.json();
    },
    onSuccess: ({ saved }: { saved: boolean }) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/ad-animator/history`],
      });
      toast({ title: saved ? "Saved" : "Removed from saved" });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't update", description: e.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const res = await apiRequest(
        "DELETE",
        `/api/brands/${brandId}/ad-animator/jobs/${jobId}`,
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/ad-animator/history`],
      });
      toast({ title: "Deleted" });
    },
    onError: (e: any) => {
      toast({ title: "Delete failed", description: e.message, variant: "destructive" });
    },
  });

  const renameMutation = useMutation({
    mutationFn: async ({ jobId, name }: { jobId: string; name: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/ad-animator/jobs/${jobId}/name`,
        { name },
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/ad-animator/history`],
      });
    },
    onError: (e: any) => {
      toast({ title: "Rename failed", description: e.message, variant: "destructive" });
    },
  });

  // Regenerate: re-roll an existing animation (even a successful one) into a
  // FRESH job, optionally tweaking settings/prompt first. The previous result
  // stays in History.
  const regenerateMutation = useMutation({
    mutationFn: async ({
      jobId,
      body,
    }: {
      jobId: string;
      body: Record<string, unknown>;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/ad-animator/jobs/${jobId}/regenerate`,
        body,
      );
      return res.json();
    },
    onSuccess: (data: { jobId: string }) => {
      setRegenJob(null);
      setActiveJobId(data.jobId);
      if (data.jobId) {
        setSessionJobIds((prev) =>
          prev.includes(data.jobId) ? prev : [...prev, data.jobId],
        );
      }
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/ad-animator/history`],
      });
      toast({ title: "Regenerating", description: "Re-rolling a fresh animation." });
    },
    onError: (e: any) => {
      toast({ title: "Regenerate failed", description: e.message, variant: "destructive" });
    },
  });

  // Phone-review actions: keep (save) or trash (delete), then advance the queue.
  const reviewKeep = (jobId: string) => {
    saveMutation.mutate({ jobId, saved: true });
    setReviewedJobIds((prev) => (prev.includes(jobId) ? prev : [...prev, jobId]));
  };
  const reviewTrash = (jobId: string) => {
    deleteMutation.mutate(jobId);
    setReviewedJobIds((prev) => (prev.includes(jobId) ? prev : [...prev, jobId]));
  };

  const canGenerate =
    selectedAdIds.length > 0 &&
    !generateMutation.isPending &&
    (musicStyle !== "Custom" || customMusicStyle.trim().length > 0);

  const toggleAd = (id: string) =>
    setSelectedAdIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const adImageUrl = (ad: LibraryAd) => ad.durableUrl ?? ad.imageUrlOriginal ?? undefined;

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Clapperboard className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
            Ad Animator
          </h1>
          <p className="text-sm text-muted-foreground">
            Turn a saved static ad into a scroll-stopping animated video.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="mb-6 flex items-center gap-3" data-testid="steps-indicator">
        <StepPill index={1} label="Select ads" active={step === 1} done={step > 1} />
        <div className="h-px w-8 bg-border" />
        <StepPill index={2} label="Settings & animate" active={step === 2} done={false} />
      </div>

      {/* STEP 1 — Select ads */}
      {step === 1 && (
        <Card data-testid="card-step-select">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Select saved ads</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Pick one or more saved ads to animate. Each ad becomes its own video.
              </p>
            </div>
            {ads.length > 0 && !adsLoading && !adsError && (
              <span className="text-sm text-muted-foreground" data-testid="text-ads-available">
                {ads.length} available
              </span>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search / sort / filter bar — mirrors the My Ads library. */}
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                <FilterSelect
                  value={personaId}
                  onChange={setPersonaId}
                  placeholder="Persona"
                  testId="select-persona"
                  options={personas.map((p) => ({ value: p.id, label: p.name }))}
                />
                <FilterSelect
                  value={productId}
                  onChange={setProductId}
                  placeholder="Product"
                  testId="select-product"
                  options={products.map((p) => ({ value: p.id, label: p.name }))}
                />
                <FilterSelect
                  value={angleId}
                  onChange={setAngleId}
                  placeholder="Narrative angle"
                  testId="select-angle"
                  options={angles.map((a) => ({ value: a.id, label: a.name }))}
                />
                <FilterSelect
                  value={adUnitId}
                  onChange={setAdUnitId}
                  placeholder="Ad unit"
                  testId="select-ad-unit"
                  options={adUnits
                    .filter((u) => u.formatType === "static")
                    .map((u) => ({ value: u.id, label: u.name }))}
                />
                <FilterSelect
                  value={sort}
                  onChange={(v) => setSort(v as "newest" | "oldest")}
                  placeholder="Sort"
                  testId="select-sort"
                  includeAll={false}
                  options={[
                    { value: "newest", label: "Newest first" },
                    { value: "oldest", label: "Oldest first" },
                  ]}
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search ad name or prompt…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-9 pl-8 text-sm"
                    data-testid="input-search"
                  />
                </div>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearch("");
                      setPersonaId("all");
                      setProductId("all");
                      setAngleId("all");
                      setAdUnitId("all");
                    }}
                    data-testid="button-clear-filters"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </div>

            {adsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : adsError ? (
              <div className="flex flex-col items-center gap-3 py-14 text-center">
                <AlertCircle className="h-6 w-6 text-destructive" />
                <p className="text-sm text-muted-foreground">
                  Couldn't load saved ads. Check your connection and try again.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchAds()}
                  data-testid="button-retry-ads"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              </div>
            ) : ads.length === 0 ? (
              <p className="py-14 text-center text-sm text-muted-foreground" data-testid="text-no-ads">
                {hasActiveFilters
                  ? "No saved ads match your filters. Try clearing them."
                  : "No saved ads found. Save some ads in My Ads first."}
              </p>
            ) : (
              <div className="grid max-h-[55vh] grid-cols-3 gap-3 overflow-y-auto p-1 sm:grid-cols-4 md:grid-cols-5">
                {displayedAds.map((ad) => {
                  const selected = selectedAdIds.includes(ad.id);
                  return (
                    <button
                      key={ad.id}
                      onClick={() => toggleAd(ad.id)}
                      className={`group relative overflow-hidden rounded-lg border-2 transition ${
                        selected
                          ? "border-primary"
                          : "border-transparent hover:border-muted-foreground/30"
                      }`}
                      data-testid={`button-ad-${ad.id}`}
                      aria-pressed={selected}
                    >
                      <img
                        src={adImageUrl(ad)}
                        alt="Ad"
                        className="aspect-square w-full object-cover"
                      />
                      {selected && <span className="absolute inset-0 bg-primary/20" />}
                      <span
                        className={`absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full transition ${
                          selected
                            ? "bg-primary text-primary-foreground"
                            : "bg-background/80 text-transparent ring-1 ring-border group-hover:text-muted-foreground"
                        }`}
                      >
                        <Check className="h-3 w-3" />
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-between border-t pt-4">
              <span className="text-sm text-muted-foreground" data-testid="text-selected-count">
                {selectedAdIds.length} selected
              </span>
              <div className="flex gap-2">
                {selectedAdIds.length > 0 && (
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedAdIds([])}
                    data-testid="button-clear-selection"
                  >
                    Clear
                  </Button>
                )}
                <Button
                  className="gap-2"
                  disabled={selectedAdIds.length === 0}
                  onClick={() => setStep(2)}
                  data-testid="button-continue"
                >
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2 — Settings & animate */}
      {step === 2 && (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <Card data-testid="card-step-configure">
            <CardHeader>
              <CardTitle>Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Selected ads summary */}
              <div className="space-y-3 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {selectedAds.length} ad{selectedAds.length === 1 ? "" : "s"} selected
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => setStep(1)}
                    data-testid="button-back-to-select"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Change
                  </Button>
                </div>
                {selectedAds.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedAds.map((ad) => (
                      <div key={ad.id} className="relative" data-testid={`thumb-selected-${ad.id}`}>
                        <img
                          src={adImageUrl(ad)}
                          alt="Selected ad"
                          className="h-16 w-16 rounded object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => toggleAd(ad.id)}
                          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-background text-muted-foreground shadow ring-1 ring-border hover:text-destructive"
                          data-testid={`button-remove-ad-${ad.id}`}
                          aria-label="Remove ad"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No ads selected. Go back to pick at least one.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Each ad generates its own animation
                  {variations > 1 ? ` (${variations} variations each)` : ""}.
                </p>
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label>Duration</Label>
                <Select
                  value={String(duration)}
                  onValueChange={(v) => setDuration(Number(v) as AdAnimatorDuration)}
                >
                  <SelectTrigger data-testid="select-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AD_ANIMATOR_DURATIONS.map((d) => (
                      <SelectItem key={d} value={String(d)}>
                        {d} seconds
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Aspect ratio */}
              <div className="space-y-2">
                <Label>Aspect ratio</Label>
                <Select
                  value={aspectRatio}
                  onValueChange={(v) => setAspectRatio(v as AdAnimatorAspectRatio)}
                >
                  <SelectTrigger data-testid="select-aspect-ratio">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AD_ANIMATOR_ASPECT_RATIOS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Animation intensity */}
              <div className="space-y-2">
                <Label>Animation intensity</Label>
                <Select value={intensity} onValueChange={(v) => setIntensity(v as AdAnimatorIntensity)}>
                  <SelectTrigger data-testid="select-intensity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AD_ANIMATOR_INTENSITIES.map((i) => (
                      <SelectItem key={i} value={i}>
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Music style */}
              <div className="space-y-2">
                <Label>Music style</Label>
                <Select value={musicStyle} onValueChange={(v) => setMusicStyle(v as AdAnimatorMusicStyle)}>
                  <SelectTrigger data-testid="select-music-style">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AD_ANIMATOR_MUSIC_STYLES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {musicStyle === "Custom" && (
                  <Input
                    placeholder="Describe the music style"
                    value={customMusicStyle}
                    onChange={(e) => setCustomMusicStyle(e.target.value)}
                    data-testid="input-custom-music"
                  />
                )}
              </div>

              {/* Variations */}
              <div className="space-y-2">
                <Label>Variations</Label>
                <Select value={String(variations)} onValueChange={(v) => setVariations(Number(v))}>
                  <SelectTrigger data-testid="select-variations">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AD_ANIMATOR_VARIATION_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} {n === 1 ? "variation" : "variations"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Prompt editing — layered. By default users add a short note
                  that's appended to the proven base prompt; the toggle exposes
                  the full prompt for a verbatim freeform override. */}
              <div className="space-y-2 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1.5">
                    <Wand2 className="h-3.5 w-3.5" />
                    Prompt
                  </Label>
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor="toggle-full-prompt"
                      className="text-xs font-normal text-muted-foreground"
                    >
                      Edit full prompt
                    </Label>
                    <Switch
                      id="toggle-full-prompt"
                      checked={fullPromptMode}
                      onCheckedChange={(on) => {
                        setFullPromptMode(on);
                        // Seed the editor with the current base so users tweak
                        // rather than start from a blank box.
                        if (on && !promptOverride.trim()) setPromptOverride(basePrompt);
                      }}
                      data-testid="switch-full-prompt"
                    />
                  </div>
                </div>

                {fullPromptMode ? (
                  <>
                    <Textarea
                      value={promptOverride}
                      onChange={(e) => setPromptOverride(e.target.value)}
                      rows={6}
                      className="text-xs font-mono"
                      placeholder="Write the full prompt sent to the video model…"
                      data-testid="textarea-full-prompt"
                    />
                    <p className="text-xs text-muted-foreground">
                      Used verbatim — your text replaces the base prompt entirely.
                    </p>
                  </>
                ) : (
                  <>
                    <Textarea
                      value={customInstructions}
                      onChange={(e) => setCustomInstructions(e.target.value)}
                      rows={2}
                      placeholder="Add instructions (optional) — e.g. slow push-in, no text"
                      data-testid="textarea-add-instructions"
                    />
                    <details className="text-xs text-muted-foreground">
                      <summary
                        className="cursor-pointer select-none"
                        data-testid="summary-prompt-preview"
                      >
                        Preview prompt
                      </summary>
                      <p
                        className="mt-1 whitespace-pre-wrap font-mono"
                        data-testid="text-prompt-preview"
                      >
                        {basePrompt}
                      </p>
                    </details>
                  </>
                )}
              </div>

              <Button
                className="w-full gap-2"
                disabled={!canGenerate}
                onClick={() => generateMutation.mutate()}
                data-testid="button-generate"
              >
                {generateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Clapperboard className="h-4 w-4" />
                )}
                {selectedAdIds.length > 1
                  ? `Generate ${selectedAdIds.length} animations`
                  : "Generate animation"}
              </Button>
            </CardContent>
          </Card>

          {/* Phone mockup to the right of Settings — shows the ad as a Facebook
              post, then plays the rendered animation to keep or discard,
              Creative-Tinder style. */}
          <div className="lg:sticky lg:top-6">
            <AnimatorPhone
              brandId={brandId}
              brandName={brandName}
              brandLogo={brandLogo}
              siteHost={siteHost}
              reviewJob={pendingReview[0] ?? null}
              remaining={pendingReview.length}
              generating={runningJobs.length > 0}
              previewImageUrl={
                runningJobs[0]?.sourceImageUrl ||
                (selectedAds[0] ? adImageUrl(selectedAds[0]) : undefined)
              }
              onKeep={() => {
                const j = pendingReview[0];
                if (j) reviewKeep(j.id);
              }}
              onTrash={() => {
                const j = pendingReview[0];
                if (j) reviewTrash(j.id);
              }}
              busy={saveMutation.isPending || deleteMutation.isPending}
            />
          </div>
        </div>
      )}

      {/* History / Saved. Freshly generated animations are reviewed up top in
          the phone beside Settings; this list is the full History and the
          Saved set. */}
      <Card className="mt-6">
        <CardHeader className="space-y-4">
          <div className="flex items-center gap-1" data-testid="tabs-animations">
            <button
              type="button"
              onClick={() => setView("history")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                view === "history"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              }`}
              data-testid="tab-history"
            >
              History
            </button>
            <button
              type="button"
              onClick={() => setView("saved")}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                view === "saved"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              }`}
              data-testid="tab-saved"
            >
              <Bookmark className="h-3.5 w-3.5" />
              Saved{savedCount > 0 ? ` (${savedCount})` : ""}
            </button>
          </div>

          {/* Search + sort for the History / Saved lists. */}
          <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by persona, product, angle…"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  className="h-9 pl-8 text-sm"
                  data-testid="input-history-search"
                />
              </div>
              <div className="w-40 shrink-0">
                <FilterSelect
                  value={historySort}
                  onChange={(v) => setHistorySort(v as "newest" | "oldest")}
                  placeholder="Sort"
                  testId="select-history-sort"
                  includeAll={false}
                  options={[
                    { value: "newest", label: "Newest first" },
                    { value: "oldest", label: "Oldest first" },
                  ]}
                />
              </div>
            </div>
        </CardHeader>
        <CardContent>
          {displayedJobs.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="text-no-history">
              {view === "saved"
                ? "No saved animations yet. Tap the bookmark on an animation to save it."
                : historySearch.trim()
                  ? "No animations match your search."
                  : "No animations yet. Pick a saved ad to get started."}
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {displayedJobs.map((job) => (
                <HistoryJobCard
                  key={job.id}
                  job={job}
                  onRetry={() => retryMutation.mutate(job.id)}
                  retrying={retryMutation.isPending}
                  onSave={() => saveMutation.mutate({ jobId: job.id, saved: !job.saved })}
                  savingPending={saveMutation.isPending}
                  onDelete={() => {
                    if (window.confirm("Delete this animation? This can't be undone.")) {
                      deleteMutation.mutate(job.id);
                    }
                  }}
                  deletingPending={deleteMutation.isPending}
                  onRename={(name) => renameMutation.mutate({ jobId: job.id, name })}
                  onRegenerate={() => setRegenJob(job)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {regenJob && (
        <RegenerateDialog
          key={regenJob.id}
          job={regenJob}
          submitting={regenerateMutation.isPending}
          onClose={() => {
            if (!regenerateMutation.isPending) setRegenJob(null);
          }}
          onSubmit={(body) =>
            regenerateMutation.mutate({ jobId: regenJob.id, body })
          }
        />
      )}
    </div>
  );
}

// Regenerate dialog — seeded from an existing job so the user can tweak the
// settings and prompt before re-rolling a fresh animation. The previous job
// stays in History untouched.
function RegenerateDialog({
  job,
  submitting,
  onClose,
  onSubmit,
}: {
  job: AnimationJob;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [duration, setDuration] = useState<AdAnimatorDuration>(
    (AD_ANIMATOR_DURATIONS.includes(job.duration as any)
      ? job.duration
      : AD_ANIMATOR_DURATIONS[0]) as AdAnimatorDuration,
  );
  const [aspectRatio, setAspectRatio] = useState<AdAnimatorAspectRatio>(
    (AD_ANIMATOR_ASPECT_RATIOS.includes(job.aspectRatio as any)
      ? job.aspectRatio
      : "Original") as AdAnimatorAspectRatio,
  );
  const [intensity, setIntensity] = useState<AdAnimatorIntensity>(
    (AD_ANIMATOR_INTENSITIES.includes(job.animationIntensity as any)
      ? job.animationIntensity
      : "Standard") as AdAnimatorIntensity,
  );
  const [musicStyle, setMusicStyle] = useState<AdAnimatorMusicStyle>(
    (AD_ANIMATOR_MUSIC_STYLES.includes(job.musicStyle as any)
      ? job.musicStyle
      : "No Music") as AdAnimatorMusicStyle,
  );
  const [customMusicStyle, setCustomMusicStyle] = useState(job.customMusicStyle ?? "");
  const [variations, setVariations] = useState<number>(job.variations ?? 1);
  const [customInstructions, setCustomInstructions] = useState(
    job.customInstructions ?? "",
  );
  // Open in full-prompt mode if the source job used a verbatim override.
  const [fullPromptMode, setFullPromptMode] = useState(Boolean(job.promptOverride));
  const [promptOverride, setPromptOverride] = useState(job.promptOverride ?? "");

  const basePrompt = useMemo(
    () =>
      buildAdAnimatorPrompt({
        musicStyle,
        customMusicStyle: musicStyle === "Custom" ? customMusicStyle : null,
        intensity,
        customInstructions,
      }),
    [musicStyle, customMusicStyle, intensity, customInstructions],
  );

  const canSubmit =
    !submitting && (musicStyle !== "Custom" || customMusicStyle.trim().length > 0);

  const handleSubmit = () => {
    onSubmit({
      duration,
      aspectRatio,
      animationIntensity: intensity,
      musicStyle,
      customMusicStyle: musicStyle === "Custom" ? customMusicStyle : undefined,
      variations,
      // Always send these so an explicit clear (empty string) is honored.
      customInstructions: customInstructions.trim(),
      promptOverride: fullPromptMode ? promptOverride : "",
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="dialog-regenerate">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4" />
            Regenerate animation
          </DialogTitle>
          <DialogDescription>
            Re-roll a fresh animation from this ad. Tweak the settings or prompt
            first — your previous result stays in History.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Duration</Label>
              <Select
                value={String(duration)}
                onValueChange={(v) => setDuration(Number(v) as AdAnimatorDuration)}
              >
                <SelectTrigger data-testid="select-regen-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AD_ANIMATOR_DURATIONS.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d}s
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Aspect ratio</Label>
              <Select
                value={aspectRatio}
                onValueChange={(v) => setAspectRatio(v as AdAnimatorAspectRatio)}
              >
                <SelectTrigger data-testid="select-regen-aspect">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AD_ANIMATOR_ASPECT_RATIOS.map((a) => (
                    <SelectItem key={a} value={a}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Motion intensity</Label>
              <Select
                value={intensity}
                onValueChange={(v) => setIntensity(v as AdAnimatorIntensity)}
              >
                <SelectTrigger data-testid="select-regen-intensity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AD_ANIMATOR_INTENSITIES.map((i) => (
                    <SelectItem key={i} value={i}>
                      {i}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Variations</Label>
              <Select
                value={String(variations)}
                onValueChange={(v) => setVariations(Number(v))}
              >
                <SelectTrigger data-testid="select-regen-variations">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AD_ANIMATOR_VARIATION_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} {n === 1 ? "variation" : "variations"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Music style</Label>
            <Select
              value={musicStyle}
              onValueChange={(v) => setMusicStyle(v as AdAnimatorMusicStyle)}
            >
              <SelectTrigger data-testid="select-regen-music">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AD_ANIMATOR_MUSIC_STYLES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {musicStyle === "Custom" && (
              <Input
                placeholder="Describe the music style"
                value={customMusicStyle}
                onChange={(e) => setCustomMusicStyle(e.target.value)}
                data-testid="input-regen-custom-music"
              />
            )}
          </div>

          {/* Prompt editing — same layered model as the main panel. */}
          <div className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5">
                <Wand2 className="h-3.5 w-3.5" />
                Prompt
              </Label>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="toggle-regen-full-prompt"
                  className="text-xs font-normal text-muted-foreground"
                >
                  Edit full prompt
                </Label>
                <Switch
                  id="toggle-regen-full-prompt"
                  checked={fullPromptMode}
                  onCheckedChange={(on) => {
                    setFullPromptMode(on);
                    if (on && !promptOverride.trim()) setPromptOverride(basePrompt);
                  }}
                  data-testid="switch-regen-full-prompt"
                />
              </div>
            </div>

            {fullPromptMode ? (
              <>
                <Textarea
                  value={promptOverride}
                  onChange={(e) => setPromptOverride(e.target.value)}
                  rows={6}
                  className="text-xs font-mono"
                  placeholder="Write the full prompt sent to the video model…"
                  data-testid="textarea-regen-full-prompt"
                />
                <p className="text-xs text-muted-foreground">
                  Used verbatim — your text replaces the base prompt entirely.
                </p>
              </>
            ) : (
              <>
                <Textarea
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  rows={2}
                  placeholder="Add instructions (optional) — e.g. slow push-in, no text"
                  data-testid="textarea-regen-add-instructions"
                />
                <details className="text-xs text-muted-foreground">
                  <summary
                    className="cursor-pointer select-none"
                    data-testid="summary-regen-prompt-preview"
                  >
                    Preview prompt
                  </summary>
                  <p
                    className="mt-1 whitespace-pre-wrap font-mono"
                    data-testid="text-regen-prompt-preview"
                  >
                    {basePrompt}
                  </p>
                </details>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="button-regen-cancel"
          >
            Cancel
          </Button>
          <Button
            className="gap-2"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="button-regen-submit"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4" />
            )}
            Regenerate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepPill({
  index,
  label,
  active,
  done,
}: {
  index: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 text-sm font-medium ${
        active ? "text-foreground" : "text-muted-foreground"
      }`}
      data-testid={`step-${index}`}
    >
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
          active
            ? "bg-primary text-primary-foreground"
            : done
            ? "bg-primary/15 text-primary"
            : "bg-muted text-muted-foreground"
        }`}
      >
        {done ? <Check className="h-3.5 w-3.5" /> : index}
      </span>
      {label}
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  placeholder,
  options,
  testId,
  includeAll = true,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  testId: string;
  includeAll?: boolean;
}) {
  return (
    <SearchableSelect
      value={value}
      onValueChange={onChange}
      placeholder={placeholder}
      testId={testId}
      triggerClassName="h-9 text-sm"
      options={[
        ...(includeAll ? [{ value: "all", label: `All ${placeholder.toLowerCase()}` }] : []),
        ...options,
      ]}
    />
  );
}

function formatJobDate(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function HistoryJobCard({
  job,
  onRetry,
  retrying,
  onSave,
  savingPending,
  onDelete,
  deletingPending,
  onRename,
  onRegenerate,
}: {
  job: AnimationJob;
  onRetry: () => void;
  retrying: boolean;
  onSave: () => void;
  savingPending: boolean;
  onDelete: () => void;
  deletingPending: boolean;
  onRename: (name: string) => void;
  onRegenerate: () => void;
}) {
  const fallbackName =
    [job.productName, job.personaName].filter(Boolean).join(" · ") ||
    job.angleName ||
    "Animated ad";
  const displayName = job.name?.trim() || fallbackName;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(job.name ?? "");
  const cancelRef = useRef(false);
  const meta = [job.personaName, job.productName].filter(Boolean).join(" · ");
  const dateStr = formatJobDate(job.createdAt as any);

  const startEdit = () => {
    cancelRef.current = false;
    setDraft(job.name ?? "");
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    const trimmed = draft.trim();
    if (trimmed !== (job.name ?? "").trim()) onRename(trimmed);
  };
  const cancel = () => {
    cancelRef.current = true;
    setDraft(job.name ?? "");
    setEditing(false);
  };

  return (
    <div
      className="flex flex-col rounded-lg border p-2.5"
      data-testid={`row-history-${job.id}`}
    >
      <div className="mb-1.5 flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          {editing ? (
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") cancel();
              }}
              className="h-7 text-sm"
              placeholder={fallbackName}
              data-testid={`input-job-name-${job.id}`}
            />
          ) : (
            <button
              type="button"
              onClick={startEdit}
              className="group flex w-full items-center gap-1 text-left"
              title="Click to rename"
              data-testid={`button-rename-${job.id}`}
            >
              <span className="truncate text-sm font-medium" data-testid={`text-job-name-${job.id}`}>
                {displayName}
              </span>
              <Pencil className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          )}
          {meta && (
            <p className="truncate text-xs text-muted-foreground" data-testid={`text-job-meta-${job.id}`}>
              {meta}
            </p>
          )}
          <p className="text-xs capitalize text-muted-foreground">
            {[dateStr, `${job.duration}s`, job.aspectRatio].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="flex shrink-0 items-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onSave}
            disabled={savingPending}
            title={job.saved ? "Remove from saved" : "Save"}
            data-testid={`button-save-${job.id}`}
          >
            <Bookmark className={`h-4 w-4 ${job.saved ? "fill-primary text-primary" : ""}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            disabled={deletingPending}
            title="Delete"
            data-testid={`button-delete-${job.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="mb-1.5 flex flex-wrap items-center gap-1">
        {(job.status === "failed" || job.status === "partial") && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={onRetry}
            disabled={retrying}
            data-testid={`button-retry-${job.id}`}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </Button>
        )}
        {/* Regenerate is available for any finished job (re-rolls a fresh
            animation, even from a successful one). */}
        {(job.status === "completed" ||
          job.status === "failed" ||
          job.status === "partial" ||
          job.status === "cancelled") && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={onRegenerate}
            data-testid={`button-regenerate-${job.id}`}
          >
            <Wand2 className="h-3.5 w-3.5" />
            Regenerate
          </Button>
        )}
      </div>
      <JobVideos job={job} onRetry={onRetry} retrying={retrying} />
    </div>
  );
}

function JobVideos({
  job,
  onRetry,
  retrying,
}: {
  job: AnimationJob;
  onRetry: () => void;
  retrying: boolean;
}) {
  const videos = job.videos || [];
  return (
    <div className="space-y-2">
      {/* Source ad this animation was made from. */}
      {job.sourceImageUrl && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-1.5">
          <img
            src={job.sourceImageUrl}
            alt="Source ad"
            className="h-9 w-9 shrink-0 rounded object-cover"
            data-testid={`img-source-${job.id}`}
          />
          <span className="text-xs text-muted-foreground">Source ad</span>
        </div>
      )}

      {/* The exact prompt sent to the video model for this animation. */}
      {job.promptUsed && (
        <details
          className="rounded-md border bg-muted/30 p-1.5 text-xs"
          data-testid={`prompt-used-${job.id}`}
        >
          <summary className="cursor-pointer select-none text-muted-foreground">
            Prompt used
          </summary>
          <p className="mt-1.5 whitespace-pre-wrap break-words text-foreground/80">
            {job.promptUsed}
          </p>
        </details>
      )}

      {videos.length === 0 ? (
        <p className="text-sm text-muted-foreground">Preparing variations…</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {videos.map((v) => (
            <div key={v.id} className="space-y-1.5" data-testid={`video-output-${v.id}`}>
              {v.status === "ready" && v.videoUrl ? (
                <>
                  <video
                    src={v.videoUrl}
                    poster={v.thumbnailUrl || job.sourceImageUrl || undefined}
                    controls
                    loop
                    preload="none"
                    className="mx-auto max-h-64 w-auto max-w-full rounded-md border bg-black"
                    data-testid={`player-${v.id}`}
                  />
                  <a href={v.videoUrl} download target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" className="h-7 w-full gap-1 text-xs" data-testid={`button-download-${v.id}`}>
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </Button>
                  </a>
                </>
              ) : v.status === "failed" ? (
                <div className="flex aspect-video flex-col items-center justify-center gap-1.5 rounded-md border border-dashed text-center">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  <span className="px-2 text-xs text-muted-foreground">
                    {v.errorMessage || "This variation failed."}
                  </span>
                  <Button variant="ghost" size="sm" className="gap-1" onClick={onRetry} disabled={retrying}>
                    <RefreshCw className="h-3.5 w-3.5" />
                    Retry
                  </Button>
                </div>
              ) : (
                <div className="flex aspect-video flex-col items-center justify-center gap-1.5 rounded-md border border-dashed text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-xs">Variation {v.variationNumber}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// AnimatorPhone — a Facebook-feed phone mockup shown to the right of the Step 2
// settings, Creative-Tinder style. Before generating it previews the selected
// ad as a "Sponsored" post; while a job renders it shows that ad with a spinner;
// once a fresh render is ready it plays the animation and lets the user keep
// (save) or discard (delete) it.
function AnimatorPhone({
  brandId,
  brandName,
  brandLogo,
  siteHost,
  reviewJob,
  remaining,
  generating,
  previewImageUrl,
  onKeep,
  onTrash,
  busy,
}: {
  brandId: string;
  brandName: string;
  brandLogo: string | null;
  siteHost: string;
  reviewJob: AnimationJob | null;
  remaining: number;
  generating: boolean;
  previewImageUrl?: string;
  onKeep: () => void;
  onTrash: () => void;
  busy: boolean;
}) {
  const reviewVideo = reviewJob
    ? (reviewJob.videos || []).find((v) => v.status === "ready" && v.videoUrl)
    : undefined;
  const mode: "review" | "generating" | "preview" = reviewJob
    ? "review"
    : generating
      ? "generating"
      : "preview";

  const mediaImage = reviewJob?.sourceImageUrl || previewImageUrl || undefined;

  // Sound handling for the review player. Browsers block autoplay *with* sound,
  // so the clip autoplays muted and the user enables sound with a tap (the
  // standard social-feed pattern). We only surface the affordance when the job
  // actually has music — a "No Music" clip has no audio track to unmute.
  const videoRef = useRef<HTMLVideoElement>(null);
  const [soundOn, setSoundOn] = useState(false);
  const hasMusic = !!reviewJob?.musicStyle && reviewJob.musicStyle !== "No Music";
  // Reset to muted whenever we advance to a different review clip.
  useEffect(() => {
    setSoundOn(false);
  }, [reviewVideo?.id]);
  const enableSound = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = false;
    el.volume = 1;
    setSoundOn(true);
    void el.play().catch(() => {});
  };

  // Generate realistic FB ad copy (primary text + headline) from the source
  // image so the mockup reads like a live post. Keyed on the image so each ad
  // gets its own copy, cached across mode changes. Best-effort: falls back to
  // placeholder copy if the request fails or is still loading.
  const productName = reviewJob?.productName || undefined;
  const { data: adCopy } = useQuery<{ primaryText: string; headline: string }>({
    queryKey: ["/api/brands", brandId, "ad-animator/ad-copy", mediaImage, brandName, productName],
    enabled: !!brandId && !!mediaImage,
    staleTime: 60 * 60 * 1000,
    retry: 2,
    queryFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/ad-animator/ad-copy`,
        { imageUrl: mediaImage, brandName, productName },
      );
      return res.json();
    },
  });

  const postCopy =
    adCopy?.primaryText?.trim() ||
    (mode === "review"
      ? reviewJob?.name?.trim() ||
        [reviewJob?.productName, reviewJob?.personaName].filter(Boolean).join(" · ") ||
        "Your animation is ready"
      : mode === "generating"
        ? "Bringing your ad to life…"
        : "See your ad come to life");

  const ctaHeadline = adCopy?.headline?.trim() || brandName;

  return (
    <div className="flex flex-col items-center gap-4" data-testid="animator-phone">
      <div className="text-xs font-medium text-muted-foreground" data-testid="text-phone-status">
        {mode === "review" ? (
          <span>
            <strong className="text-foreground">{remaining}</strong> to review
          </span>
        ) : mode === "generating" ? (
          "Rendering…"
        ) : (
          "Facebook preview"
        )}
      </div>

      {/* Phone shell */}
      <div className="relative w-[340px] max-w-full rounded-[40px] bg-neutral-900 p-2.5 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.05)] select-none">
        <span aria-hidden className="absolute left-[-2px] top-20 h-10 w-[3px] rounded-l bg-neutral-800" />
        <span aria-hidden className="absolute left-[-2px] top-32 h-14 w-[3px] rounded-l bg-neutral-800" />
        <span aria-hidden className="absolute right-[-2px] top-28 h-16 w-[3px] rounded-r bg-neutral-800" />
        <div className="relative overflow-hidden rounded-[30px] bg-white dark:bg-neutral-950">
          {/* Status bar + dynamic island */}
          <div className="relative flex items-center justify-between px-5 pt-2 pb-1 text-[10px] font-semibold text-neutral-900 dark:text-neutral-100">
            <span>9:41</span>
            <span aria-hidden className="absolute left-1/2 top-2 z-10 h-5 w-20 -translate-x-1/2 rounded-full bg-black" />
            <span aria-hidden className="h-2.5 w-5 rounded-[2px] border border-neutral-400/70" />
          </div>

          {/* Facebook top bar */}
          <div className="flex items-center justify-between border-b border-neutral-200 px-3 py-1.5 dark:border-neutral-800">
            <span className="text-[15px] font-extrabold lowercase text-[#1877F2]">facebook</span>
            <Search className="h-4 w-4 text-neutral-500" />
          </div>

          {/* Sponsored post */}
          <div className="flex flex-col">
            {/* Post header */}
            <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-200 text-[11px] font-bold text-neutral-500 dark:bg-neutral-800">
                <ImgWithFallback
                  src={brandLogo}
                  className="h-full w-full object-cover"
                  fallback={(brandName.trim().charAt(0) || "?").toUpperCase()}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 truncate text-[13px] font-semibold text-neutral-900 dark:text-neutral-100">
                  {brandName}
                  <Check className="h-3 w-3 shrink-0 text-[#1877F2]" strokeWidth={3} />
                </div>
                <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
                  Sponsored · 🌐
                </div>
              </div>
              <MoreHorizontal className="h-4 w-4 text-neutral-500" />
            </div>

            {/* Post copy */}
            <div className="line-clamp-3 px-3 pb-2 text-[13px] leading-snug text-neutral-900 dark:text-neutral-100" data-testid="text-phone-copy">
              {postCopy}
            </div>

            {/* Media: source image (preview/generating) or rendered video (review) */}
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-neutral-100 dark:bg-neutral-900">
              {mode === "review" && reviewVideo?.videoUrl ? (
                <>
                  <video
                    key={reviewVideo.id}
                    ref={videoRef}
                    src={reviewVideo.videoUrl}
                    controls
                    autoPlay
                    loop
                    muted={!soundOn}
                    playsInline
                    onVolumeChange={(e) => {
                      // Keep our overlay in sync when the user toggles mute via
                      // the native controls instead of the overlay button.
                      const el = e.currentTarget;
                      setSoundOn(!el.muted && el.volume > 0);
                    }}
                    className="absolute inset-0 h-full w-full bg-black object-contain"
                    data-testid={`phone-player-${reviewJob?.id}`}
                  />
                  {hasMusic && !soundOn && (
                    <button
                      type="button"
                      onClick={enableSound}
                      className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-xs font-medium text-white shadow-lg backdrop-blur transition hover:bg-black/85"
                      data-testid={`button-phone-unmute-${reviewJob?.id}`}
                    >
                      <Volume2 className="h-3.5 w-3.5" />
                      Tap for sound
                    </button>
                  )}
                </>
              ) : mediaImage ? (
                <>
                  <img
                    src={mediaImage}
                    alt="Ad preview"
                    className="absolute inset-0 h-full w-full object-cover"
                    draggable={false}
                    data-testid="img-phone-preview"
                  />
                  {mode === "generating" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/45 text-white">
                      <Loader2 className="h-7 w-7 animate-spin" />
                      <span className="text-xs font-medium">Rendering animation…</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-neutral-400">
                  <ImageIcon className="h-8 w-8" />
                  <span className="text-xs">Select an ad to preview</span>
                </div>
              )}
            </div>

            {/* CTA bar */}
            <div className="flex items-center justify-between border-y border-neutral-200 bg-neutral-100 px-3 py-2 dark:border-neutral-800 dark:bg-neutral-900">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[10px] uppercase tracking-wide text-neutral-500">
                  {siteHost}
                </div>
                <div className="truncate text-[12px] font-bold text-neutral-900 dark:text-neutral-100" data-testid="text-phone-headline">
                  {ctaHeadline}
                </div>
              </div>
              <span className="ml-3 shrink-0 rounded-md bg-neutral-200 px-3 py-1.5 text-[12px] font-semibold text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100">
                Shop Now
              </span>
            </div>

            {/* Reactions row */}
            <div className="grid grid-cols-3 border-t border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium text-neutral-600 dark:text-neutral-300">
                <ThumbsUp className="h-3.5 w-3.5" /> Like
              </div>
              <div className="flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium text-neutral-600 dark:text-neutral-300">
                <MessageCircle className="h-3.5 w-3.5" /> Comment
              </div>
              <div className="flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium text-neutral-600 dark:text-neutral-300">
                <Share2 className="h-3.5 w-3.5" /> Share
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Keep / discard — only when there's a fresh render to review. */}
      {mode === "review" ? (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={onTrash}
              disabled={busy}
              className="flex h-14 w-14 items-center justify-center rounded-full border border-rose-200 bg-card text-rose-500 shadow-md transition hover:scale-105 hover:bg-rose-50 active:scale-95 disabled:opacity-40 disabled:hover:scale-100 disabled:hover:bg-card"
              data-testid={`button-phone-trash-${reviewJob?.id}`}
              aria-label="Discard"
            >
              <X className="h-7 w-7" strokeWidth={2.5} />
            </button>
            <div className="select-none text-[10px] uppercase tracking-widest text-muted-foreground">
              discard · keep
            </div>
            <button
              type="button"
              onClick={onKeep}
              disabled={busy}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-md transition hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100"
              data-testid={`button-phone-keep-${reviewJob?.id}`}
              aria-label="Keep"
            >
              <Bookmark className="h-6 w-6" strokeWidth={2.5} fill="currentColor" />
            </button>
          </div>
          {reviewVideo?.videoUrl && (
            <a href={reviewVideo.videoUrl} download target="_blank" rel="noreferrer">
              <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" data-testid={`button-phone-download-${reviewJob?.id}`}>
                <Download className="h-3.5 w-3.5" />
                Download
              </Button>
            </a>
          )}
        </div>
      ) : (
        <p className="max-w-[340px] text-center text-xs text-muted-foreground">
          {mode === "generating"
            ? "Your animation is rendering — it'll appear here to keep or discard."
            : "This is how your ad looks on Facebook. Generate to see it animate."}
        </p>
      )}
    </div>
  );
}
